import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getVerificationConfiguration } from "@redux/selectors/verification-configuration-selectors";
import { Logger } from "@utils/logger";
import { setDownloadingAcuantSdkFailed, setDownloadingAcuantSdkSuccess, setIsDownloadingAcuantSdk } from "@redux/actions/sdk-actions";
import { loadScript } from "@utils/script-util";
import { getVerificationStepConfig } from "@redux/selectors/verification-step-config-selectors";
import { CaptureModes } from "@enums/capture-modes";
import { setCaptureMode } from "@redux/actions/verification-step-config-actions";
import { getSdkStateSelector } from "@redux/selectors/sdk-selectors";
import { ACUANT_SDK_APP_ID_ENDPOINT } from "@constants/index";
import { setError } from "@redux/actions/error-actions";
import { getQueryParametersState } from "@redux/selectors/query-parameters-selectors";
import { navigateToUtil } from "@utils/navigation-util";
import { useNavigate } from "react-router-dom";

declare global {
    interface Window {
        acuantConfig: { path: string; };
        loadAcuantSdk: () => void;
        onAcuantSdkLoaded: () => void;
    }
}

export const useInitializeAcuantSdk = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const sdkState = useSelector(getSdkStateSelector);
    const verificationConfiguration = useSelector(getVerificationConfiguration);
    const verificationStepConfig = useSelector(getVerificationStepConfig);
    const queryParametersState = useSelector(getQueryParametersState);
    const [startTime, setStartTime] = useState<number>(null);
    const [acuantJavascriptWebSdkLoaded, setAcuantJavascriptWebSdkLoaded] = useState(false);
    const [acuantJavascriptWebSdkInitialized, setAcuantJavascriptWebSdkInitialized] = useState(false);
    const [acuantCameraLoaded, setAcuantCameraLoaded] = useState(false);
    const [acuantPassiveLivenessLoaded, setAcuantPassiveLivenessLoaded] = useState(false);
    const [openCVScriptLoaded, setOpenCVScriptLoaded] = useState(false);

    const onAcuantJavascriptSdkLoaded = () => {
        const authorizationToken = verificationConfiguration.properties?.acuantToken;
        if (authorizationToken) {
            AcuantJavascriptWebSdk.initializeWithToken(authorizationToken, ACUANT_SDK_APP_ID_ENDPOINT, {
                onSuccess: () => {
                    Logger.info(`[AcuantSdkService] SDK initialization Success`);
                    try {
                        AcuantJavascriptWebSdk.start(() => {
                            Logger.info(`[AcuantSdkService] initialize succeded`);
                            setAcuantJavascriptWebSdkInitialized(true);
                        }, true);
                    } catch (error) {
                        Logger.error(`[AcuantSdkSerivce] Error starting workers: ${error}`);
                        setAcuantJavascriptWebSdkInitialized(true);
                    }
                },
                onFail: (code: string, description: string) => {
                    Logger.error(`[AcuantSdkService] SDK initialization error code ${code}: \n${description}`);
                    dispatch(setDownloadingAcuantSdkFailed());
                }
            });
        } else {
            Logger.error(`[AcuantSdkService] SDK initialization error: Acuant SDK Token not provided`);
            dispatch(setDownloadingAcuantSdkFailed());
            dispatch(setError({
                name: "Acuant SDK Token Error",
                message: 'Critical: Acuant SDK Token was not provided',
                asi: queryParametersState.token,
                errorCode: "ACUANT_SDK_INITIALIZATION_ERROR",
                httpStatus: "400",
                errorURI: window.location.href
            }));
            navigateToUtil("/error", navigate, location.pathname);
        }
    };

    const onAcuantJavascriptWebSdkScriptLoaded = () => {
        setAcuantJavascriptWebSdkLoaded(true);
        const endTime = new Date().getTime();
        Logger.info(`[AcuantSdkService] AcuantJavascriptWebSdk load time: ${(endTime - startTime) / 1000000000000} seconds`);

        window.onAcuantSdkLoaded = () => {
            Logger.info(`[AcuantSdkService] Acuant SDK onAcuantSdkLoaded invoked`);
            onAcuantJavascriptSdkLoaded();
        };

        window.loadAcuantSdk();
    };

    const onAcuantCameraScriptLoaded = () => {
        setAcuantCameraLoaded(true);
        const endTime = new Date().getTime();
        Logger.info(`[AcuantSdkService] AcuantCamera load time: ${(endTime - startTime) / 1000000000000} seconds`);
    };

    const onAcuantPassiveLivenessScriptLoaded = () => {
        setAcuantPassiveLivenessLoaded(true);
        const endTime = new Date().getTime();
        Logger.info(`[AcuantSdkService] AcuantPassiveLiveness load time: ${(endTime - startTime) / 1000000000000} seconds`);
    };

    const onOpenCVScriptLoaded = () => {
        setOpenCVScriptLoaded(true);
        const endTime = new Date().getTime();
        Logger.info(`[AcuantSdkService] OpenCV load time: ${(endTime - startTime) / 1000000000000} seconds`);
    };

    const fetchAcuantSdk = () => {
        Logger.info("[DocumentCaptureEffects] Fetch Acuant SDK");
        Logger.info('[AcuantSdkService] Loading SDK');

        try {
            dispatch(setIsDownloadingAcuantSdk());
            loadScript("/dcui/assets/acuant/AcuantJavascriptWebSdk.min.js", "AcuantJavascriptWebSdk", onAcuantJavascriptWebSdkScriptLoaded);
            loadScript("/dcui/assets/acuant/AcuantCamera.js", "AcuantCamera", onAcuantCameraScriptLoaded);
            loadScript("/dcui/assets/acuant/AcuantPassiveLiveness.min.js", "AcuantPassiveLiveness", onAcuantPassiveLivenessScriptLoaded);
            loadScript("/dcui/assets/acuant/opencv.min.js", "OpenCV", onOpenCVScriptLoaded);
        } catch (error) {
            console.error(error);
            dispatch(setDownloadingAcuantSdkFailed());
            Logger.info("[AcuantSdkService] SDK script load error")
        }
    };

    const initializeAcuantSdk = () => {
        const useAcuantSDKCapture = verificationConfiguration.properties?.useAcuantSDKCapture;

        if (useAcuantSDKCapture) {
            window.acuantConfig = { path: "/dcui/assets/acuant/" };
            setStartTime(new Date().getTime());
            fetchAcuantSdk();
        }
    };

    // after download state update, set capture mode
    useEffect(() => {
        if (sdkState.hasDownloadedAcuantSDK) {
            dispatch(setCaptureMode(CaptureModes.ACUANT_SDK));
        }
    }, [sdkState.hasDownloadedAcuantSDK])

    // after download completed, update state
    useEffect(() => {
        if (
            acuantJavascriptWebSdkLoaded &&
            acuantJavascriptWebSdkInitialized &&
            acuantCameraLoaded &&
            acuantPassiveLivenessLoaded &&
            openCVScriptLoaded
        ) {
            dispatch(setDownloadingAcuantSdkSuccess());
        }
    }, [
        acuantJavascriptWebSdkLoaded,
        acuantCameraLoaded,
        acuantPassiveLivenessLoaded,
        openCVScriptLoaded,
        acuantJavascriptWebSdkInitialized
    ]);

    // after step config loaded, initialize SDK if necessary
    useEffect(() => {
        if (verificationStepConfig.initialized) {
            initializeAcuantSdk();
        }
    }, [verificationStepConfig.initialized]);
};